import { get } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";

import { PER_PAGE_DEFAULT } from "@lhd/constants";
import {
  useFetchClients,
  useFetchEvents,
  useFetchProjects,
  useFetchTasksByProject,
} from "@lhd/hooks";
import { Event, IOptions, Projects } from "@lhd/types";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { SelectOptionsProject } from ".";
import { getTimezoneString } from "../Apps/utils";
import { user } from "../users/selector";

export type ReceivedProps = Record<string, any>;

export const PROGRESS = {
  new: "新規",
  inprogress: "着手",
  finished: "解決",
  reject: "拒否",
  close: "完了",
};

export const PROGESS_KEY = {
  new: "new",
  inprogress: "inprogress",
  finished: "finished",
  reject: "reject",
  close: "close",
};

export const PROGESS_COLOR = {
  new: "#ed8077",
  inprogress: "#868cb7",
  finished: "#dc9925",
  reject: "#EA2C00",
  close: "#a1af2f",
};

const useProjectManagement = (props: ReceivedProps) => {
  const [currentDate, setCurrentDate] = useState<any>(new Date());
  const [searchParams, setSearchParams] = useSearchParams();
  const isShowCalendarParams = searchParams.get("tab") === "calendar";

  const [isShowCalendar, setIsShowCalendar] =
    useState<boolean>(isShowCalendarParams);
  const [earliestStartAt, setEarliestStartAt] = useState<string>("");
  const [preStepsCount, setPreStepsCount] = useState<number>(1);
  const [tasks, setTasks] = useState<any>([]);
  const [pieChartData, setPieChartData] = useState<any>([]);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [projects, setProjects] = useState<Projects[]>([]);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [filter, setFilter] = useState<Record<string, string | any>>({});
  const [clientOptions, setClientOptions] = useState<IOptions>();
  const [userId, setUserId] = useState<string | number | null>();
  const [valueSelectProject, setValueSelectProject] = useState<string>("");

  const auth = useSelector(user);

  const filterParams = useMemo(() => {
    const dataFilter: any = {};
    if (filter?.name_cont) {
      dataFilter["q[name_cont]"] = filter?.name_cont;
    }
    if (filter?.client_name_cont) {
      dataFilter["q[client_name_cont]"] = filter?.client_name_cont;
    }
    if (filter?.note_cont) {
      dataFilter["q[note_cont]"] = filter?.note_cont;
    }
    if (filter?.client_id) {
      dataFilter["q[client_id_eq]"] = filter?.client_id;
    }

    return dataFilter;
  }, [filter]);

  const {
    data: projectData,
    isSuccess: isProjectSuccess,
    isFetching: isFetchProject,
  } = useFetchProjects(page, 10, filterParams);

  const {
    data: clientData,
    isSuccess: isCompnaySuccess,
    isFetching: isFetchingClient,
  } = useFetchClients(1, PER_PAGE_DEFAULT, {});

  const {
    data: taskData,
    isSuccess: isTaskSuccess,
    isFetching: isFetchTask,
  } = useFetchTasksByProject(
    undefined,
    moment(currentDate)
      .endOf("month")
      .set("month", new Date(currentDate).getMonth() - 1)
      .format("YYYY-MM-DD"),
    moment(currentDate).endOf("month").format("YYYY-MM-DD"),
    userId,
  );

  const { data: eventData } = useFetchEvents(
    moment(currentDate)
      .endOf("month")
      .set("month", new Date(currentDate).getMonth() - 1)
      .format("YYYY-MM-DD"),
    moment(currentDate).endOf("month").format("YYYY-MM-DD"),
  );

  const handleDate = (isPrev: boolean) => {
    const date = new Date(currentDate);
    const month = date.getMonth();
    const prevMonth = new Date(currentDate).setMonth(month - 1);
    const nextMonth = new Date(currentDate).setMonth(month + 1);

    if (isPrev) {
      setCurrentDate(prevMonth);
      setEarliestStartAt("");
    } else {
      setCurrentDate(nextMonth);
      setEarliestStartAt("");
    }
  };

  // const getAllProject = () => {
  //   setFilter({});
  // };

  // const getProjectAssignMe = () => {
  //   setFilter({ client_id: clientMe?.id || 1000000 });
  // };

  const onChangeSelectProject = (val: string) => {
    if (val === SelectOptionsProject[0]?.value) {
      setUserId(null);
      setValueSelectProject(SelectOptionsProject[0]?.value);
    } else if (val === SelectOptionsProject[1]?.value) {
      setUserId(auth?.id);
      setValueSelectProject(SelectOptionsProject[1]?.value);
    }
  };

  const events = useMemo(() => {
    if (!eventData?.data || eventData?.data?.length === 0) {
      return [];
    }
    const existEvent: string[] = [];
    const result: Event[] = [];
    eventData?.data?.forEach((eventItem) => {
      const stringExist =
        eventItem?.content + eventItem?.created_at + eventItem?.schedule_color;
      if (!existEvent.includes(stringExist)) {
        existEvent.push(stringExist);
        result.push(eventItem);
      }
    });

    return result;
  }, [eventData?.data]);

  useEffect(() => {
    const result = tasks.reduce((a: any, v: any) => {
      if (a[v.status]) {
        a[v.status].status = [a[v.status].status, v.status].join(",");
      } else {
        a[v.status] = v;
      }
      return a;
    }, {});

    let total = 0;

    const newData = Object.values(result).map((item: any) => {
      const percent = Math.round(
        (item.status.split(",").length / tasks?.length) * 100,
      );
      total = total + percent;
      return {
        name: get(PROGRESS, item.status.split(",")[0]),
        value: percent,
        colors: item.styles.progressColor,
      };
    });

    if (newData?.length > 0 && total !== 100) {
      const valueLastItem = newData[newData.length - 1].value;
      newData[newData.length - 1].value = 100 - (total - valueLastItem);
    }

    setPieChartData(newData);
  }, [tasks]);

  useEffect(() => {
    if (isTaskSuccess && taskData?.data) {
      const startOfMonth = moment(currentDate).startOf("month");
      const endOfMonth = moment(currentDate).endOf("month");
      const timeZoneString = getTimezoneString();

      const result = taskData?.data
        ?.filter((item) => {
          return item.start_at && moment(item.start_at).isValid();
        })
        ?.map((item) => {
          const itemStartAt = moment(item.start_at)
            .utcOffset(timeZoneString)
            .startOf("day");

          const itemEndAt =
            item.end_at && moment(item.end_at).isValid()
              ? moment(item.end_at).utcOffset(timeZoneString).endOf("day")
              : moment(item.start_at).utcOffset(timeZoneString).endOf("day");

          const startDate = isShowCalendar
            ? itemStartAt.toDate()
            : itemStartAt.isSameOrBefore(startOfMonth)
            ? startOfMonth.toDate()
            : itemStartAt.toDate();

          const endDate = isShowCalendar
            ? itemEndAt.toDate()
            : itemEndAt.isSameOrAfter(endOfMonth)
            ? endOfMonth.toDate()
            : itemEndAt.toDate();

          return {
            start: startDate,
            end: endDate,
            name: item.title || "",
            id: item.id,
            type: "task",
            project: item.project,
            progress: item.progress || 0,
            status: item.status,
            styles: {
              progressColor: get(PROGESS_COLOR, item.status) || "#ccc",
            },
          };
        })
        ?.filter((task) => {
          return (
            task.start instanceof Date &&
            task.end instanceof Date &&
            !isNaN(task.start.getTime()) &&
            !isNaN(task.end.getTime()) &&
            task.start <= task.end
          );
        });

      setTasks(result);
    }
  }, [currentDate, isShowCalendar, isTaskSuccess, taskData]);

  useEffect(() => {
    const dateEarliestStartAt = moment(earliestStartAt, "YYYY-MM-DD").date();
    if (dateEarliestStartAt - 1 > 1) {
      setPreStepsCount(dateEarliestStartAt);
    } else {
      setPreStepsCount(1);
    }
  }, [currentDate, earliestStartAt]);

  useEffect(() => {
    if (isProjectSuccess) {
      setProjects(projectData.data);
      setTotal(+projectData.meta.total);
      if (!projectId) {
        setProjectId(projectData.data[0]?.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProjectSuccess, projectData]);

  useEffect(() => {
    if (page !== 1 && isProjectSuccess) {
      setProjects([...projects, ...projectData.data]);
      // setProjectId(projects[0]?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, projectData, isProjectSuccess]);

  useEffect(() => {
    if (isCompnaySuccess) {
      setClientOptions(
        clientData.data.map((item) => ({
          value: item.id,
          label: item.name || "",
        })),
      );
    } else {
      setClientOptions([]);
    }
  }, [isCompnaySuccess, clientData]);

  return {
    ...props,
    currentDate,
    tasks,
    pieChartData,
    isShowCalendar,
    projects,
    page,
    total,
    isFetchProject,
    isFetchTask,
    projectId,
    setProjectId,
    setPage,
    setIsShowCalendar,
    handleDate,
    filter,
    setFilter,
    clientOptions,
    isFetchingClient,
    // getAllProject,
    // getProjectAssignMe,
    onChangeSelectProject,
    valueSelectProject,
    events: events,
    preStepsCount,
    setSearchParams,
    searchParams,
  };
};

export type Props = ReturnType<typeof useProjectManagement>;

export default useProjectManagement;
